import type {User} from '@/entities/User/types/user';
import {api} from '@/libs/baseApi.client';

export type IntercomJwtResponse = {intercomJwt: string};

/** Сессия авторизации (access/refresh токены) */
export type AuthSession = {
  access_token: string;
  refresh_token?: string;
  expires_at?: number;
  expires_in?: number;
};

// ========== Login DTOs ==========
export type LoginDto = {
  email?: string;
  phone?: string;
  password: string;
};

export type LoginResponseDto = {
  message: string; // example: "Вход выполнен успешно"
  user: AuthUserDto | null;
  session: AuthSession | null;
  accessToken: string | null; // example: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
  userType: 'user' | 'celebrity';
};

// ========== Register Email DTOs ==========
export type RegisterEmailDto = {
  email: string; // example: "user@example.com"
  password: string; // example: "password123" - минимум 6 символов
  username: string; // example: "johndoe" - минимум 3 символа
  phone?: string; // example: "+79001234567" - опционально
  referralCode?: string; // example: "123456" - опционально
};

// ========== Register Phone DTOs ==========
export type RegisterPhoneDto = {
  phone: string; // example: "+79001234567" - формат: ^\+[1-9]\d{1,14}$
  password: string; // example: "password123" - минимум 6 символов
  username: string; // example: "johndoe" - минимум 3 символа
  email?: string; // example: "user@example.com" - опционально
  referralCode?: string; // example: "123456" - опционально
};

// ========== Register Response DTO ==========
export type RegisterResponseDto = {
  message: string; // example: "Регистрация успешна. Проверьте email для подтверждения."
  user: AuthUserDto;
};

// ========== SignOut Response DTO ==========
export type SignOutResponseDto = {
  message: string; // example: "Выход выполнен успешно"
};

// ========== Refresh Response DTO ==========
export type RefreshResponseDto = {
  message: string; // example: "Token refreshed successfully"
};

// ========== Reset Password Request DTO ==========
export type ResetPasswordRequestDto = {
  email: string; // example: "user@example.com"
};
// ========== Reset Password Response DTO ==========
export type ResetPasswordResponseDto = {
  message: string; // example: "Password reset link has been sent to email"
};

// ========== Confirm Reset Password Request DTO ==========
export type ConfirmResetPasswordRequestDto = {
  newPassword: string; // example: "password123"
  confirmPassword: string; // example: "password123"
  tokenHash?: string | null; // example: "token_hash"
  accessToken?: string | null; // example: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
  refreshToken?: string | null; // example: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
};
// ========== Confirm Reset Password Response DTO ==========
export type ConfirmResetPasswordResponseDto = {
  message: string; // example: "Password has been updated successfully"
  accessToken: string; // example: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
  user: AuthUserDto;
};

// ========== Auth User DTO ==========
export type AuthUserDto = {
  id: string;
  email?: string;
  phone?: string;
  emailConfirmed: boolean;
  phoneConfirmed: boolean;
  profile?: User | null;
};

// ========== Auth Repository ==========
export const AuthRepository = {
  async login(credentials: LoginDto): Promise<LoginResponseDto> {
    const response = await api.post<LoginResponseDto>('/auth/login', credentials);
    return response.data;
  },

  async registerEmail(data: RegisterEmailDto): Promise<RegisterResponseDto> {
    const response = await api.post<RegisterResponseDto>('/auth/register/email', data);
    return response.data;
  },

  async registerPhone(data: RegisterPhoneDto): Promise<RegisterResponseDto> {
    const response = await api.post<RegisterResponseDto>('/auth/register/phone', data);
    return response.data;
  },

  async signOut(): Promise<SignOutResponseDto> {
    const response = await api.post<SignOutResponseDto>('/auth/signout');
    return response.data;
  },

  async refresh(): Promise<RefreshResponseDto> {
    const response = await api.post<RefreshResponseDto>('/auth/refresh');
    return response.data;
  },

  async resetPassword(data: ResetPasswordRequestDto): Promise<ResetPasswordResponseDto> {
    const response = await api.post<ResetPasswordResponseDto>('/auth/reset-password', data);
    return response.data;
  },

  async confirmResetPassword(data: ConfirmResetPasswordRequestDto): Promise<ConfirmResetPasswordResponseDto> {
    const response = await api.post<ConfirmResetPasswordResponseDto>('/auth/confirm-password-reset', data);
    return response.data;
  },

  async getIntercomJwt(): Promise<IntercomJwtResponse> {
    const {data} = await api.get<IntercomJwtResponse>('/ca/auth/intercom/jwt');
    return data;
  },
};
